import { useCallback, useMemo, useRef, useState } from 'react';
import _ from 'underscore';

import { checkVideosIntermediateStatus } from '../../model';

// below fields will be impacted along with status change
const VideoChangedFieldsWithStatusUpdate = [
  'thumbnailUrl',
  'url',
  'width',
  'height',
  'durationInMS',
  'status',
];

export const useCommonVideoRepoMethods = ({
  repository,
  videoRepository,
  logger,
}) => {
  const latestTimestampRef = useRef(0);
  const [videoUpdated, setVideoUpdated] = useState({}); // sample: { 123: { .. } }
  const getVideoTaskStatus = useCallback((videoTasks) => {
    const requestTimestamp = Date.now();
    const videosToCheck = _.keys(videoTasks);

    return repository.updateVideoStatusByIds(videosToCheck).then((response) => {
      // if multipe requests are sent out, ensure to keep the result from the latest request
      let notFinishedTaskList = {};
      let newVideoUpdated = {};
      if (requestTimestamp > latestTimestampRef.current) {
        const responseKeys = [];

        response.forEach((item) => {
          responseKeys.push(item.id);
          newVideoUpdated = {
            ...newVideoUpdated,
            [item.id]: _.pick(item, VideoChangedFieldsWithStatusUpdate),
          };

          if (item && checkVideosIntermediateStatus(item)) {
            notFinishedTaskList = { ...notFinishedTaskList, [item.id]: item.status };
          }
        });

        // eslint-disable-next-line no-param-reassign
        latestTimestampRef.current = requestTimestamp;
      }

      setVideoUpdated(newVideoUpdated);

      const taskCount = Object.keys(notFinishedTaskList).length;

      return { totalCount: taskCount };
    }).catch(() => {
      // log and ignore the error
      logger.logError('Failed to get videos task status.');
    });
  }, [logger, repository]);

  const addVideoCreative = useCallback(({ videoCreative }) => {
    const promise = videoRepository.addVideoCreative({ videoCreative });
    return promise;
  }, [videoRepository]);

  const getVideo = useCallback(({ id, fetchMetadata }) => {
    const promise = videoRepository.getVideo({ id, fetchMetadata });
    return promise;
  }, [videoRepository]);

  const returnVal = useMemo(() => ({
    getVideoTaskStatus,
    videoUpdated,
    addVideoCreative,
    getVideo,
  }), [addVideoCreative, getVideo, getVideoTaskStatus, videoUpdated]);

  return returnVal;
};
